import type { BikePayload, MedicalPayload, SpecialLuggagePayload } from '../types'
import { normalizeDecimalForApi } from '@/scopes/common/components/FormElements/utils/decimalPatternUtils'

export interface SpecialLuggagePayloadMapper {
  map: (formData: Record<string, unknown>) => SpecialLuggagePayload
}

export function createSpecialLuggagePayloadMapper(): SpecialLuggagePayloadMapper {
  const map = (formData: Record<string, unknown>): SpecialLuggagePayload => {
    const { luggageType } = formData

    if (luggageType === 'bike') {
      return mapBikePayload(formData)
    }

    if (luggageType === 'medical') {
      return mapMedicalPayload(formData)
    }

    throw new Error(`Unsupported luggage type: ${luggageType}`)
  }

  return { map }
}

function mapBikePayload(formData: Record<string, unknown>): BikePayload {
  return {
    participant: formData.participant as string,
    luggageType: 'bike',
    height: normalizeDecimalForApi(formData.height as string),
    width: normalizeDecimalForApi(formData.width as string),
    length: normalizeDecimalForApi(formData.length as string),
    weight: normalizeDecimalForApi(formData.weight as string),
    packageType: formData.packageType as string,
  }
}

type FormData = Record<string, unknown>

function pick(data: FormData, from: string, to: string = from) {
  if (data[from] == null)
    return {}
  return { [to]: data[from] as string }
}

function pickDecimal(data: FormData, from: string, to: string = from) {
  if (data[from] == null)
    return {}
  return { [to]: normalizeDecimalForApi(data[from] as string) }
}

function pickBoolean(data: FormData, from: string, to: string = from) {
  if (data[from] == null)
    return {}
  return { [to]: data[from] === 'yes' }
}

function pickArray(data: FormData, from: string, to: string = from) {
  if (data[from] == null)
    return {}
  return { [to]: data[from] as string[] }
}

function mapMedicalPayload(f: FormData): MedicalPayload {
  return {
    participant: f.participant as string,
    luggageType: 'medical',
    medicalType: f.medicalEquipmentType as string,

    ...pick(f, 'otherMedicalBagType', 'medicalBagType'),
    ...pick(f, 'transportationArea'),

    ...pickBoolean(f, 'isBringingMedicine'),
    ...pickBoolean(f, 'isNeedingExtraMedicineKilos'),
    ...pickBoolean(f, 'isFoldedWheelchair'),

    ...pickDecimal(f, 'foldedWidth', 'wheelchairFoldedWidth'),
    ...pickDecimal(f, 'foldedHeight', 'wheelchairFoldedHeight'),
    ...pickDecimal(f, 'foldedLength', 'wheelchairFoldedLength'),

    ...pickBoolean(f, 'canWalk', 'isParticipantWalking'),
    ...pickBoolean(f, 'canClimbStairs', 'isParticipantClimbingStairs'),
    ...pickBoolean(f, 'canClimbAircraftStairs', 'isParticipantClimbingAirplaneStairs'),

    ...pick(f, 'wheelchairNeeds'),

    ...pickBoolean(f, 'isSeatReservationMade'),
    ...pickBoolean(f, 'isOnlineCheckInCompleted'),
    ...pickBoolean(f, 'isCpapHandLuggageCompliant'),
    ...pickBoolean(f, 'isMedicalEquipmentNeededOnBoard'),
    ...pickBoolean(f, 'isMedicalAirportAssistanceNeeded'),

    ...pickDecimal(f, 'width'),
    ...pickDecimal(f, 'height'),
    ...pickDecimal(f, 'length'),
    ...pickDecimal(f, 'weight'),

    ...pick(f, 'brandOfBattery', 'brand'),
    ...pickDecimal(f, 'voltageOfBattery', 'voltage'),
    ...pickDecimal(f, 'ampereHoursOfBattery', 'ampereHours'),
    ...pickDecimal(f, 'wattHoursOfBattery', 'wattHours'),

    ...pick(f, 'typeOfBattery'),
    ...pickDecimal(f, 'weightOfBattery'),
    ...pickBoolean(f, 'canBatteryBeDisconnected'),
    ...pickBoolean(f, 'isWheelchairTiltPossible'),
    ...pick(f, 'isDryOrWetBattery'),

    ...pickArray(f, 'accommodationSpecialRequirements'),
    ...pick(f, 'otherAccommodationSpecialRequirements'),

    termsAndConditions: f.termsAndConditions as boolean,
    medicalCertificate: f.medicalCertificate as boolean,
  }
}
